<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
    mode: { type: String, required: true },
    settings: { type: Object, required: true },
    availableTags: { type: Array, default: () => [] },
    hasEmail: { type: Boolean, default: false },
    isSaving: { type: Boolean, default: false },
    recipientCountUrl: { type: String, required: true },
    saveStatus: { type: String, required: false, default: '' },
    showSaveButton: { type: Boolean, default: true },
});

const emit = defineEmits(['update:settings', 'save']);

const showSchedulePicker = ref(false);
const recipientCount = ref(null);
const isLoadingCount = ref(false);
const automationScheduleType = ref('draft');

const isAutomationScheduledDatetime = (schedule) => (
    !!schedule && !['draft', 'now', 'send_immediately', 'schedule'].includes(schedule)
);

const automationScheduleTypes = [
    {
        label: _mft('communication:automation.schedule.inactive.title'),
        byline: _mft('communication:automation.schedule.inactive.description'),
        value: 'draft',
    },
    {
        label: _mft('communication:automation.schedule.active.title'),
        byline: _mft('communication:automation.schedule.active.description'),
        value: 'send_immediately',
    },
    {
        label: _mft('communication:automation.schedule.automationSchedule.title'),
        byline: _mft('communication:automation.schedule.automationSchedule.description'),
        value: 'schedule',
    },
];

const canSave = computed(() => {
    if (!props.hasEmail || !props.settings.name) return false;

    if (props.mode === 'newsletter' && props.settings?.schedule === 'draft') {
        return true;
    }

    if (props.mode === 'newsletter' && props.settings?.schedule !== 'draft') {
        return props.settings.targetTags.length > 0;
    }

    if (props.settings?.schedule === 'draft') {
        return true;
    }

    if (!isAutomationScheduledDatetime(props.settings?.schedule) && !['now', 'send_immediately'].includes(props.settings?.schedule)) {
        return false;
    }

    return !!props.settings.triggerTag;
});

const maybeSave = () => {
    if (canSave.value === true) {
        emit('save')
    }
}

const updateSetting = (key, value) => {
    emit('update:settings', { ...props.settings, [key]: value });
};

const updateTargetTags = (tags) => {
    updateSetting('targetTags', tags);
};

const updateExcludeTags = (tags) => {
    updateSetting('excludeTags', tags);
};

const updateTriggerTag = (tags) => {
    updateSetting('triggerTag', tags[0] || null);
};

const saveButtonLabel = computed(() => {
    if (props.mode === 'newsletter') {
        if (props.settings.schedule === 'draft') return _mft('email-ai:settings.saveWithoutSending');
        if (props.settings.schedule !== 'now') return _mft('email-ai:settings.saveAndSchedule');

        return _mft('email-ai:settings.saveAndSend');
    }

    if (props.settings.schedule === 'draft') {
        return _mft('communication:automation.save');
    }

    if (!['now', 'send_immediately'].includes(props.settings.schedule)) {
        return _mft('communication:automation.saveAndSchedule');
    }

    return _mft('email-ai:settings.saveAndActivate');
});

const syncAutomationScheduleType = (schedule) => {
    if (props.mode !== 'automation') {
        return;
    }

    if (schedule === 'draft' || schedule === null || schedule === '') {
        automationScheduleType.value = 'draft';
        return;
    }

    if (schedule === 'now' || schedule === 'send_immediately') {
        automationScheduleType.value = 'send_immediately';
        return;
    }

    automationScheduleType.value = 'schedule';
};

const onAutomationScheduleTypeSelected = (type) => {
    automationScheduleType.value = type;

    if (type === 'draft') {
        updateSetting('schedule', 'draft');
        return;
    }

    if (type === 'send_immediately') {
        updateSetting('schedule', 'send_immediately');
        return;
    }

    if (!isAutomationScheduledDatetime(props.settings.schedule)) {
        updateSetting('schedule', 'schedule');
    }
};

const updateAutomationScheduleDate = (value) => {
    updateSetting('schedule', value || 'schedule');
};

const loadRecipientCount = async () => {
    const includedIds = props.settings.targetTags.map(t => t.id);
    const excludedIds = props.settings.excludeTags.map(t => t.id);

    if (!includedIds.length) {
        recipientCount.value = null;
        return;
    }

    isLoadingCount.value = true;
    try {
        const params = new URLSearchParams();
        includedIds.forEach(id => params.append('included_tags[]', id));
        excludedIds.forEach(id => params.append('excluded_tags[]', id));
        params.append('delivery_types[]', 'email');

        const response = await fetch(`${props.recipientCountUrl}?${params.toString()}`);
        const data = await response.json();
        recipientCount.value = data.leads_with_email_count ?? data.leads_count ?? 0;
    } catch (e) {
        console.error('Failed to load recipient count', e);
        recipientCount.value = null;
    } finally {
        isLoadingCount.value = false;
    }
};

// Watch for tag changes and reload count
watch(
    () => [
        props.settings.targetTags.map(t => t.id).join(','),
        props.settings.excludeTags.map(t => t.id).join(','),
    ],
    () => {
        loadRecipientCount();
    },
);

watch(
    () => props.settings.schedule,
    (schedule) => {
        syncAutomationScheduleType(schedule);
    },
    { immediate: true },
);
</script>

<template>
    <div class="px-4 pb-4 space-y-3">
        <ui-tabbed :track-state="false" :responsive="false" slot-container-classes="pt-3">
            <ui-tabbed-tab
                :title="_mft('shared:settings')"
                value="configuration"
                :active="true"
            >
                <div class="space-y-3">
                    <!-- Name -->
                    <div>
                        <label class="block font-medium text-gray-600 mb-1">
                            <mf-translate domainkey="email-ai:settings.name"></mf-translate>
                        </label>
                        <input
                            :value="settings.name"
                            @input="updateSetting('name', $event.target.value)"
                            :placeholder="_mft('email-ai:settings.namePlaceholder')"
                            class="w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:border-transparent"
                            :class="mode === 'newsletter' ? 'focus:ring-aiAgentNewsletterSpecialist' : 'focus:ring-aiAgentAutomationExpert'"
                        />
                    </div>

                    <!-- Newsletter-specific settings -->
                    <template v-if="mode === 'newsletter'">
                        <!-- Schedule -->
                        <div>
                            <label class="block font-medium text-gray-600 mb-1">
                                <mf-translate domainkey="email-ai:settings.schedule"></mf-translate>
                            </label>
                            <div class="flex items-center gap-2">
                                <component
                                    :is="settings.schedule === 'draft' ? 'button-primary' : 'button-default'"
                                    @click="updateSetting('schedule', 'draft'); showSchedulePicker = false"
                                    size="sm"
                                >{{ _mft('email-ai:settings.saveWithoutSending') }}</component>
                                <component
                                    :is="settings.schedule === 'now' ? 'button-primary' : 'button-default'"
                                    @click="updateSetting('schedule', 'now'); showSchedulePicker = false"
                                    size="sm"
                                >{{ _mft('email-ai:settings.sendNow') }}</component>
                                <component
                                    :is="settings.schedule !== 'now' && settings.schedule !== 'draft' ? 'button-primary' : 'button-default'"
                                    @click="showSchedulePicker = !showSchedulePicker"
                                    size="sm"
                                >{{ _mft('email-ai:settings.pickDate') }}</component>
                            </div>
                            <input
                                v-if="showSchedulePicker || (settings.schedule !== 'now' && settings.schedule !== 'draft')"
                                type="datetime-local"
                                :value="settings.schedule !== 'now' && settings.schedule !== 'draft' ? settings.schedule : ''"
                                @input="updateSetting('schedule', $event.target.value)"
                                class="mt-2 w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2"
                                :class="mode === 'newsletter' ? 'focus:ring-aiAgentNewsletterSpecialist' : 'focus:ring-aiAgentAutomationExpert'"
                            />
                        </div>
                    </template>

                    <!-- Automation-specific settings -->
                    <template v-if="mode === 'automation'">
                        <!-- Delay -->
                        <div>
                            <div class="mb-1 flex items-center gap-1">
                                <label class="block font-medium text-gray-600">
                                    <mf-translate domainkey="email-ai:settings.delay"></mf-translate>
                                </label>
                                <container-with-tooltip
                                    tooltipClass="max-w-xs rounded-md bg-white px-2 py-1 text-xs text-gray-700 shadow-md"
                                    additional-tooltip-class="w-40"
                                >
                                    <template #tooltip>
                                        {{ _mft('email-ai:settings.delayTooltip') }}
                                    </template>
                                    <button
                                        type="button"
                                        class="text-xs text-gray-400 transition hover:text-gray-600"
                                    >
                                        <i class="fa-light fa-circle-info"></i>
                                    </button>
                                </container-with-tooltip>
                            </div>
                            <div class="flex items-center gap-2">
                                <input
                                    type="number"
                                    :value="settings.delayHours"
                                    @input="updateSetting('delayHours', parseInt($event.target.value) || 0)"
                                    min="0"
                                    class="w-20 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-aiAgentAutomationExpert focus:border-transparent"
                                />
                                <span class="text-xs text-gray-500">
                                    <mf-translate domainkey="email-ai:settings.hours"></mf-translate>
                                </span>
                            </div>
                        </div>

                        <div>
                            <input-radios-v2
                                :content="automationScheduleType"
                                :options="automationScheduleTypes"
                                @update:content="onAutomationScheduleTypeSelected"
                                :label="_mft('communication:automation.schedule.type')"
                                name="automationScheduleType"
                            />
                        </div>

                        <div v-if="automationScheduleType === 'schedule'">
                            <input
                                type="datetime-local"
                                :value="isAutomationScheduledDatetime(settings.schedule) ? settings.schedule : ''"
                                @input="updateAutomationScheduleDate($event.target.value)"
                                class="w-full px-2.5 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-aiAgentAutomationExpert"
                            />
                        </div>

                    </template>
                </div>
            </ui-tabbed-tab>

            <ui-tabbed-tab
                title="Tags"
                value="tags"
            >
                <div class="space-y-3">
                    <div class="">
                        <template v-if="mode === 'newsletter'">
                            <!-- Recipients -->
                            <div>
                                <label class="block font-medium text-gray-600 mb-1">
                                    <mf-translate domainkey="communication:tags.toInclude"></mf-translate>
                                    <span v-if="recipientCount !== null" class="ml-1 text-gray-400 font-normal">
                                        ({{ recipientCount }} {{ _mft('email-ai:settings.recipientCount') }})
                                    </span>
                                    <span v-if="isLoadingCount" class="ml-1 text-gray-400 font-normal">...</span>
                                </label>
                                <tags-multi-select-v2
                                    :allow-creation="true"
                                    :placeholder="_mft('email-ai:settings.selectTags')"
                                    :pre-selected-tags="settings.targetTags.map(tag => tag.id)"
                                    :display-as="'list'"
                                    :display-counts="['email']"
                                    @tagsSelected="updateTargetTags"
                                />
                            </div>
                        </template>

                        <template v-if="mode === 'automation'">
                            <div class="my-4">
                                <label class="block font-medium text-gray-600 mb-1">
                                    <mf-translate domainkey="email-ai:settings.trigger"></mf-translate>
                                </label>
                                <tags-multi-select-v2
                                    :allow-creation="true"
                                    :placeholder="_mft('email-ai:settings.selectTriggerTag')"
                                    :pre-selected-tags="settings.triggerTag ? [settings.triggerTag.id] : []"
                                    :select-multiple="false"
                                    :display-as="'list'"
                                    @tagsSelected="updateTriggerTag"
                                />
                            </div>
                        </template>

                        <div class="my-4">
                            <label class="block font-medium text-gray-600 mb-1">
                                <mf-translate domainkey="communication:tags.toExclude"></mf-translate>
                            </label>
                            <tags-multi-select-v2
                                :allow-creation="true"
                                :placeholder="_mft('email-ai:settings.optional')"
                                :pre-selected-tags="settings.excludeTags.map(tag => tag.id)"
                                :display-as="'list'"
                                :display-counts="mode === 'newsletter' ? ['email'] : undefined"
                                @tagsSelected="updateExcludeTags"
                            />
                        </div>
                    </div>

                </div>
            </ui-tabbed-tab>
        </ui-tabbed>

        <!-- Save button -->
        <button-success
            v-if="showSaveButton"
            @click="maybeSave"
            :state="saveStatus"
            :disabled="!canSave || isSaving"
            size="lg"
            class="w-full justify-center"
        >
            <span v-if="isSaving">
                <mf-translate domainkey="email-ai:settings.saving"></mf-translate>
            </span>
            <span v-else>{{ saveButtonLabel }}</span>
        </button-success>
    </div>
</template>
